import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useBatchUpload } from '../Batch/hooks/useBatchUpload';
import '../Batch/Batch.css';

// The TV series screen does one thing: episodes in, series analysis out (docs/design/series-analysis-design-v0-5.md).
// Plain words on purpose: one word for one thing ("episode", "series report", "credit").

const TERMINAL = ['done', 'done_without_report', 'cancelled', 'failed'];
const STATUS_TEXT = {
    extracting: 'Reading each episode',
    contexts: 'Connecting the episodes',
    analyzing: 'Analyzing each episode',
    reporting: 'Writing the series report',
    done: 'Done',
    done_without_report: 'Done, but the series report could not be written (its credit was refunded)',
    cancel_requested: 'Cancelling',
    draining: 'Cancelling: waiting for analyses already running to finish',
    cancelled: 'Cancelled',
    failed: 'Stopped',
};
const EP_TEXT = { pending: 'Waiting', running: 'Analyzing', done: 'Done', failed: 'Could not start (credit refunded)', uncertain: 'Checking, we will sort this out' };

const naturalCompare = (a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()) + Math.random());

function EpisodeRow({ id, index, total, title, onMove, onRemove, disabled }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });
    const style = { transform: CSS.Transform.toString(transform), transition };
    return (
        <li ref={setNodeRef} style={style} className={`list-group-item d-flex align-items-center series-episode ${isDragging ? 'dragging' : ''}`}>
            <span className="drag-handle" {...attributes} {...listeners} aria-label={`Drag to reorder ${title}`} title="Drag to reorder">⠿</span>
            <span className="episode-number">{index + 1}.</span>
            <span className="flex-grow-1">{title}</span>
            <button className="btn btn-sm btn-light" aria-label="Move up" onClick={() => onMove(-1)} disabled={disabled || index === 0}>▲</button>
            <button className="btn btn-sm btn-light ms-1" aria-label="Move down" onClick={() => onMove(1)} disabled={disabled || index === total - 1}>▼</button>
            <button className="btn btn-sm btn-link text-danger ms-1" aria-label={`Remove ${title}`} title="Remove this episode" onClick={onRemove} disabled={disabled}>✕</button>
        </li>
    );
}

function Question({ label, help, value, onChange, name }) {
    return (
        <div className="mb-2">
            <div className="fw-semibold">{label}</div>
            {help && <div className="small text-muted mb-1">{help}</div>}
            {[['yes', 'Yes'], ['no', 'No'], ['unknown', 'Not sure']].map(([v, t]) => (
                <label key={v} className="me-3">
                    <input type="radio" name={name} value={v} checked={value === v} onChange={() => onChange(v)} /> {t}
                </label>
            ))}
        </div>
    );
}

// What a series analysis gives, each line opening that part of a public example report in a new tab
// (SeriesShowcase). Shown before the first run, while the writer decides whether to spend the credits.
function WhatYouGet({ example }) {
    if (!example) return null;
    const parts = [
        ['#verdicts', 'What each reader concluded about the season'],
        ['#map', 'A season map: every storyline across the episodes'],
        ['#season-notes', 'What carries across episodes'],
        ['#layers', 'The Layered Read, from the whole season down to each scene'],
        ['#characters', 'Characters across the episodes'],
    ];
    return (
        <div className="card mb-4" style={{ background: '#f4f8fb', borderColor: '#d6e6ef' }}>
            <div className="card-body py-3">
                <div className="fw-semibold mb-1" style={{ color: '#14536e' }}>What you get: every episode's own analysis, plus a report on the season</div>
                <ul className="mb-2 small ps-3">
                    {parts.map(([anchor, label]) => (
                        <li key={anchor}><a href={example.url + anchor} target="_blank" rel="noopener">{label}</a></li>
                    ))}
                </ul>
                <a href={example.url} target="_blank" rel="noopener" className="small fw-semibold">See the full example: {example.name} ({example.episodes} episodes) ↗</a>
            </div>
        </div>
    );
}

export default function SeriesScreen({ batch: initialBatch, user: initialUser, limits, example = null }) {
    const [user, setUser] = useState(initialUser);
    const [name, setName] = useState(initialBatch.name);
    const [run, setRun] = useState(initialBatch.series || null);
    const [message, setMessage] = useState(null);   // {type: 'error'|'success', text}
    const showError = (text) => setMessage({ type: 'error', text });
    const showSuccess = (text) => setMessage({ type: 'success', text });
    const fileInputRef = useRef(null);

    const { uploadQueue, localMovies, setLocalMovies, handleFileSelect } = useBatchUpload(initialBatch, showError, showSuccess);
    const episodes = localMovies.filter(m => m.source === 'server' || (!m.source && m.id));
    const inFlight = localMovies.filter(m => m.source === 'uploading' || m.source === 'failed');

    // A trip to buy credits reloads this page: keep the order and the answers below for this series in this tab.
    const draftKey = `series-draft-${initialBatch.uuid}`;
    const draft = useMemo(() => { try { return JSON.parse(sessionStorage.getItem(draftKey) || '{}') || {}; } catch (e) { return {}; } }, []);

    // Order: what the user arranged; new episodes are added in filename order (Episode 2 before Episode 10).
    const [order, setOrder] = useState(Array.isArray(draft.order) ? draft.order : []);
    useEffect(() => {
        const ids = [...episodes].sort((a, b) => naturalCompare(a.filename || a.title, b.filename || b.title)).map(m => m.id);
        setOrder(prev => {
            const kept = prev.filter(id => ids.includes(id));
            const added = ids.filter(id => !prev.includes(id));
            return kept.length === prev.length && added.length === 0 ? prev : [...kept, ...added];
        });
    }, [localMovies]);
    const byId = useMemo(() => Object.fromEntries(episodes.map(m => [m.id, m])), [localMovies]);

    const [edition, setEdition] = useState(draft.edition || 'standard');
    const [coverage, setCoverage] = useState(draft.coverage || 'unknown');
    const [pilot, setPilot] = useState(draft.pilot || 'unknown');
    const [endpoint, setEndpoint] = useState(draft.endpoint || 'unknown');
    useEffect(() => {
        try { sessionStorage.setItem(draftKey, JSON.stringify({ order, edition, coverage, pilot, endpoint })); } catch (e) { /* storage off: nothing to keep */ }
    }, [order, edition, coverage, pilot, endpoint]);
    const [busy, setBusy] = useState(false);
    const [requestKey, setRequestKey] = useState(newKey);   // a retried or double-clicked start is the same start

    const running = run && !TERMINAL.includes(run.status);
    // After a finished run, show its outcome first; the start form only on request.
    const [showForm, setShowForm] = useState(!run || ['cancelled', 'failed'].includes(run.status));
    useEffect(() => { if (run && ['done', 'done_without_report'].includes(run.status)) setShowForm(false); }, [run?.status]);
    const locked = !!running;
    useEffect(() => {
        if (!running) return;
        const t = setInterval(async () => {
            try { const r = await axios.get(`/series/${run.uuid}`); setRun(r.data.run); } catch (e) { /* keep last state */ }
        }, 15000);
        return () => clearInterval(t);
    }, [run?.uuid, run?.status]);

    // Dropping PDFs anywhere on the page.
    const [dragDepth, setDragDepth] = useState(0);
    const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const addFiles = (files) => {
        if (locked) { showError('These episodes are locked until the series analysis finishes.'); return; }
        const room = limits.max_episodes - episodes.length - uploadQueue.length - localMovies.filter(m => m.source === 'uploading').length;
        if (files.length > room) { showError(`A TV series can have at most ${limits.max_episodes} episodes for now.`); return; }
        handleFileSelect(files);
    };
    const drop = {
        onDragEnter: (e) => { if (!isFileDrag(e)) return; e.preventDefault(); setDragDepth(d => d + 1); },
        onDragOver: (e) => { if (!isFileDrag(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = locked ? 'none' : 'copy'; },
        onDragLeave: (e) => { if (!isFileDrag(e)) return; setDragDepth(d => Math.max(0, d - 1)); },
        onDrop: (e) => { if (!isFileDrag(e)) return; e.preventDefault(); setDragDepth(0); addFiles(Array.from(e.dataTransfer.files || [])); },
    };

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );
    const onDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setOrder(o => arrayMove(o, o.indexOf(active.id), o.indexOf(over.id)));
    };
    const move = (i, d) => setOrder(o => { const n = [...o]; const j = i + d; if (j < 0 || j >= n.length) return o; [n[i], n[j]] = [n[j], n[i]]; return n; });

    const remove = async (id) => {
        if (!confirm(`Remove "${byId[id]?.title}" from this series?`)) return;
        try {
            await axios.delete(`/movies/${id}`, { timeout: 10000 });
            setLocalMovies(prev => prev.filter(m => m.id !== id));
        } catch (e) {
            showError(e.response?.data?.error || 'Could not remove that episode.');
        }
    };

    const saveName = async () => {
        const v = name.trim();
        if (!v || v === initialBatch.name) { setName(v || initialBatch.name); return; }
        try { await axios.patch(`/batches/${initialBatch.uuid}`, { name: v }); } catch (e) { showError('Could not rename the series.'); }
    };

    const cost = order.length + 1;
    const balance = edition === 'deluxe' ? (user?.movie_credits_deluxe ?? 0) : (user?.movie_credits ?? 0);
    const editionName = edition === 'deluxe' ? 'Deep Focus' : 'Standard';
    // Credits come in packs of 5 and 10 (/buy_bulk_editions): offer the smallest pack that covers the gap,
    // preselected, and come back here after paying (return_to, validated by CheckoutController::safeReturnPath).
    const missing = Math.max(0, cost - balance);
    const pack = [5, 10].find(p => p >= missing) || 10;
    const buyUrl = `/buy_bulk_editions?edition=${edition}&qty=${pack}&return_to=${encodeURIComponent(window.location.pathname)}`;
    const uploading = uploadQueue.length > 0 || localMovies.some(m => m.source === 'uploading');
    // Starting spends the credits and cannot be stopped, so it takes the terms box and a second, explicit "yes".
    const [terms, setTerms] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const canStart = !busy && !uploading && order.length >= limits.min_episodes && order.length <= limits.max_episodes && balance >= cost && terms;

    const start = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`/batches/${initialBatch.uuid}/series`, {
                movie_ids: order, edition, coverage, starts_with_pilot: pilot, endpoint, request_key: requestKey, terms: true,
            });
            setConfirming(false);
            try { sessionStorage.removeItem(draftKey); } catch (e) { /* nothing kept */ }
            setRun(r.data.run);
            setRequestKey(newKey());
            const col = edition === 'deluxe' ? 'movie_credits_deluxe' : 'movie_credits';
            setUser(u => ({ ...u, [col]: (u?.[col] ?? 0) - cost }));
            showSuccess('Series analysis started.');
        } catch (e) {
            showError(e.response?.data?.error || e.response?.data?.message || 'Could not start the series analysis.');
        } finally { setBusy(false); }
    };
    const retryReport = async () => {
        setBusy(true);
        try {
            const r = await axios.post(`/series/${run.uuid}/retry-report`);
            setRun(r.data.run);
            const col = run.edition === 'deluxe' ? 'movie_credits_deluxe' : 'movie_credits';
            setUser(u => ({ ...u, [col]: (u?.[col] ?? 0) - 1 }));
            showSuccess('Writing the series report.');
        } catch (e) {
            showError(e.response?.data?.error || 'Could not start the series report.');
        } finally { setBusy(false); }
    };

    return (
        <div className={`container mt-4 mb-5 position-relative ${dragDepth > 0 ? 'batch-drop-active' : ''}`} style={{ maxWidth: 820 }} {...drop}>
            {dragDepth > 0 && <div className="batch-drop-overlay">{locked ? 'Episodes are locked while the analysis runs' : 'Drop your episodes to upload them'}</div>}

            <a href="/my-scripts" className="small text-decoration-none">← My Scripts</a>
            <div className="d-flex align-items-center mt-1 mb-3">
                <span className="badge bg-info text-dark me-2">TV series</span>
                <input className="form-control form-control-lg border-0 px-1 fw-bold" value={name} aria-label="Series name"
                    onChange={e => setName(e.target.value)} onBlur={saveName} onKeyDown={e => e.key === 'Enter' && e.target.blur()} />
            </div>

            {message && (
                <div className={`alert ${message.type === 'error' ? 'alert-danger' : 'alert-success'} py-2 d-flex justify-content-between`}>
                    <span>{message.text}</span>
                    <button className="btn-close" aria-label="Close" onClick={() => setMessage(null)} />
                </div>
            )}

            {running && (
                <div className="card mb-3 border-primary">
                    <div className="card-body">
                        <h5 className="card-title">{STATUS_TEXT[run.status] || run.status}</h5>
                        <ol className="mb-2">
                            {run.episodes.map(e => <li key={e.position}>{e.title}: {EP_TEXT[e.analysis] || e.analysis}</li>)}
                        </ol>
                        <div className="small text-muted mb-2">This usually takes a while. You can leave this page; the analysis keeps going. The episodes are locked until it finishes.</div>
                    </div>
                </div>
            )}

            {run && run.status === 'done' && run.has_report && (
                <div className="alert alert-success">
                    <div className="fw-semibold mb-1">Your series report is ready. <a href={`/series/${run.uuid}/report`}>Open the series report</a></div>
                    <div className="small">Each episode's own analysis: {run.episodes.map((e, i) => (
                        <span key={e.position}>{i > 0 && ' · '}<a href={`/disp?movieid=${e.hash}`} target="_blank" rel="noopener">{e.title}</a></span>
                    ))}</div>
                </div>
            )}
            {run && run.status === 'done_without_report' && (
                <div className="alert alert-warning">
                    <div className="fw-semibold mb-1">Each episode's analysis is ready, but the series report could not be written. Its credit was refunded.</div>
                    <button className="btn btn-primary btn-sm my-2" disabled={busy} onClick={retryReport}>
                        {busy ? 'Starting…' : 'Write the series report (1 credit)'}
                    </button>
                    <div className="small">{run.episodes.filter(e => e.analysis === 'done').map((e, i) => (
                        <span key={e.position}>{i > 0 && ' · '}<a href={`/disp?movieid=${e.hash}`} target="_blank" rel="noopener">{e.title}</a></span>
                    ))}</div>
                </div>
            )}

            {!running && showForm && !run && <WhatYouGet example={example} />}

            {!running && showForm && (
                <>
                    <div className="mb-4">
                        <h5 className="mb-1">1. Add the episodes</h5>
                        <div className="batch-drop-hint py-3" role="button" onClick={() => fileInputRef.current?.click()}>
                            <i className="bi bi-cloud-arrow-up"></i>
                            <div><strong>Drag the episode PDFs here</strong>, or click to choose them.</div>
                            <div className="small text-muted">Up to {limits.max_episodes} episodes, each at most {limits.max_pages} pages.</div>
                        </div>
                        <input ref={fileInputRef} type="file" multiple accept="application/pdf" style={{ display: 'none' }}
                            onChange={e => { addFiles(Array.from(e.target.files || [])); e.target.value = null; }} />
                        {(inFlight.length > 0 || uploadQueue.length > 0) && (
                            <ul className="list-unstyled small mb-0">
                                {inFlight.map(m => (
                                    <li key={m.id} className={m.source === 'failed' ? 'text-danger' : 'text-muted'}>
                                        {m.source === 'failed' ? `Could not upload ${m.filename}: ${m.errorPreview || 'failed'}` : `Uploading ${m.filename}…`}
                                        {m.source === 'failed' && <button className="btn btn-link btn-sm p-0 ms-2" onClick={() => setLocalMovies(p => p.filter(x => x.id !== m.id))}>Dismiss</button>}
                                    </li>
                                ))}
                                {uploadQueue.map(f => <li key={f.name} className="text-muted">Waiting to upload {f.name}</li>)}
                            </ul>
                        )}
                    </div>

                    {order.length > 0 && (
                        <div className="mb-4">
                            <h5 className="mb-1">2. Put them in order</h5>
                            <div className="small text-muted mb-2">Drag an episode by its handle, or use the arrows. Episode 1 goes at the top.</div>
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                                <SortableContext items={order} strategy={verticalListSortingStrategy}>
                                    <ol className="list-group">
                                        {order.map((id, i) => (
                                            <EpisodeRow key={id} id={id} index={i} total={order.length} disabled={busy}
                                                title={byId[id]?.title || byId[id]?.filename}
                                                onMove={d => move(i, d)} onRemove={() => remove(id)} />
                                        ))}
                                    </ol>
                                </SortableContext>
                            </DndContext>
                        </div>
                    )}

                    {order.length >= limits.min_episodes && (
                        <>
                            <div className="mb-4">
                                <h5 className="mb-1">3. A few questions <span className="text-muted fw-normal small">(optional)</span></h5>
                                <Question name="pilot" label="Is the first episode the pilot?" value={pilot} onChange={setPilot} />
                                <Question name="coverage" label="Did you include every episode up to the last one here?"
                                    help="If some are missing, we won't treat something as unexplained just because it happened in an episode we didn't see."
                                    value={coverage} onChange={setCoverage} />
                                <Question name="endpoint" label="Is the last episode a season or series finale?"
                                    help="Only then do we look at whether storylines that were due got resolved."
                                    value={endpoint} onChange={setEndpoint} />
                            </div>

                            <div className="mb-3">
                                <h5 className="mb-1">4. Start</h5>
                                <select className="form-select form-select-sm w-auto mb-2" value={edition} onChange={e => setEdition(e.target.value)} aria-label="Analysis type">
                                    <option value="standard">Standard analysis</option>
                                    <option value="deluxe">Deep Focus analysis</option>
                                </select>
                                <div className="mb-2">
                                    This uses <strong>{cost} {editionName} credits</strong>: one per episode ({order.length}) plus one for the series report.
                                    <div className={balance < cost ? 'text-danger fw-semibold' : 'text-muted'}>
                                        You have {balance} {editionName} credit{balance === 1 ? '' : 's'}{balance < cost ? `, so you need ${missing} more.` : '.'}
                                    </div>
                                    {balance < cost && (
                                        <div className="mt-2">
                                            <a className="btn btn-outline-primary btn-sm" href={buyUrl}>Buy {pack} {editionName} credits</a>
                                            <div className="text-muted small mt-1">
                                                {pack < missing ? `That pack covers ${pack}; you'll still need ${missing - pack} more. ` : ''}
                                                After paying you come back to this page, with your episodes and choices as you left them.
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="form-check mb-2">
                                    <input className="form-check-input" type="checkbox" id="series-terms" checked={terms} onChange={e => { setTerms(e.target.checked); setConfirming(false); }} />
                                    <label className="form-check-label" htmlFor="series-terms">
                                        <strong>I agree to the <a href="/terms.php" target="_blank" rel="noopener">Terms and Conditions</a></strong>
                                        <div className="small text-muted">Your intellectual property rights remain yours. We only analyze your scripts to provide feedback.</div>
                                    </label>
                                </div>
                                {!confirming ? (
                                    <button className="btn btn-primary" disabled={!canStart} onClick={() => setConfirming(true)}>
                                        {uploading ? 'Waiting for uploads…' : 'Analyze series'}
                                    </button>
                                ) : (
                                    <div className="border rounded-2 p-3" style={{ background: '#f4f8fb', maxWidth: 560 }}>
                                        <div className="fw-semibold mb-1">Start the analysis of {order.length} episodes?</div>
                                        <div className="small mb-2">This uses {cost} {editionName} credits now. Once it starts it can't be stopped, and the episodes stay locked until it finishes.</div>
                                        <button className="btn btn-primary btn-sm me-2" disabled={!canStart} onClick={start}>{busy ? 'Starting…' : 'Yes, start'}</button>
                                        <button className="btn btn-link btn-sm" disabled={busy} onClick={() => setConfirming(false)}>Not yet</button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                    {order.length === 1 && <div className="text-muted small">Add at least one more episode to analyze them as a series.</div>}
                </>
            )}
        </div>
    );
}
